/* eslint-disable react/no-this-in-sfc */
import React from 'react';

import { Flex } from '@chakra-ui/react';
import { CardContent } from '@mesh.ai/rabbit';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsMore from 'highcharts/highcharts-more';
import highchartsSolidGauge from 'highcharts/modules/solid-gauge';
import { capitalize } from 'lodash';
import _ from 'lodash/fp';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import noInsights from 'assets/assetsV1/illustrations/deerIllustrations/noInsights.svg';
import NoDataImg from 'assets/assetsV1/illustrations/noDataTrendLine.svg';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextLabel } from 'uiToolkitV1/Typography';

import { OneOnOneInsightsCompletionGraphData } from '../OneOnOneCompletion/service';
import './styles.css';

highchartsMore(Highcharts);
highchartsSolidGauge(Highcharts);

const SENTIMENT_COLOR_MAP = {
  BELONGING: { barColor: '#FFE1D9', text: 'Strongly Negative' },
  PERFORMANCE: { barColor: '#FFE7C5', text: 'Negative' },
  COLLABORATION: { barColor: '#FFEFD4', text: 'Neutral' },
  GROWTH: { barColor: '#DBF2D3', text: 'Positive' },
  MANAGER_EFFECTIVENESS: { barColor: '#BFE8C6', text: 'Strongly Positive' },
};
const OVERALL_SENTIMENT_TEXT_COLOR = {
  'Strongly Negative': '#C62C02',
  Negative: '#B96400',
  Neutral: '#8C6A00',
  Positive: '#44812F',
  'Strongly Positive': '#1A5724',
};

interface Props {
  data: OneOnOneInsightsCompletionGraphData;
  chartRef?: React.MutableRefObject<HTMLDivElement>;
  isMyDataOverviewChart?: boolean;
}

const SentimentLegend = ({ isMyDataOverviewChart }) => (
  <Flex alignItems="center" justifyContent="center" mt={isMyDataOverviewChart ? 0 : 4}>
    {Object.keys(SENTIMENT_COLOR_MAP).map((theme) => {
      const { barColor, text } = SENTIMENT_COLOR_MAP[theme];
      return (
        <Flex alignItems="center" key={theme} flexShrink={0}>
          <Flex
            style={{
              width: '15px',
              height: '15px',
              backgroundColor: barColor,
              marginRight: '8px',
            }}
          />
          <TextLabel mr="8px" color="#666666" fontSize="12px">
            {text}
          </TextLabel>
        </Flex>
      );
    })}
  </Flex>
);

// eslint-disable-next-line consistent-return
const getBubbleColor = (overAllSentimentScore) => {
  if (overAllSentimentScore < -0.6) {
    return SENTIMENT_COLOR_MAP.BELONGING.barColor;
  }
  if (overAllSentimentScore >= -0.6 && overAllSentimentScore < -0.3) {
    return SENTIMENT_COLOR_MAP.PERFORMANCE.barColor;
  }
  if (overAllSentimentScore >= -0.3 && overAllSentimentScore <= 0.3) {
    return SENTIMENT_COLOR_MAP.COLLABORATION.barColor;
  }
  if (overAllSentimentScore > 0.3 && overAllSentimentScore <= 0.6) {
    return SENTIMENT_COLOR_MAP.GROWTH.barColor;
  }
  if (overAllSentimentScore > 0.6) {
    return SENTIMENT_COLOR_MAP.MANAGER_EFFECTIVENESS.barColor;
  }
};

const ThemeDistributionChart = ({ data, chartRef, isMyDataOverviewChart }: Props) => {
  const { themeSentimentData = {} } = data ?? {};

  const chartData = Object.keys(themeSentimentData)?.map((theme) => {
    const { overAllSentimentScore, totalOneOnOnes, totalAgendaPoints, overallSentiment, sentimentDistribution } =
      themeSentimentData[theme];

    const sentimentValue = sentimentDistribution[overallSentiment.toUpperCase()];

    const bubbleColor = getBubbleColor(overAllSentimentScore);

    return {
      name: theme,
      data: [
        {
          value: totalAgendaPoints,
          name: theme,
          totalOneOnOnes,
          totalAgendaPoints,
          sentimentDistribution,
          sentimentValue,
          overallSentiment,
          color: bubbleColor,
        },
      ],
    };
  });

  const options = {
    chart: {
      type: 'packedbubble',
    },
    plotOptions: {
      packedbubble: {
        minSize: '70%',
        maxSize: '100%',
        layoutAlgorithm: {
          enableSimulation: false,
        },

        dataLabels: {
          enabled: true,
          formatter() {
            let { name } = this.point;
            const { overallSentiment } = this.point;

            if (this.point.totalAgendaPoints < 3 || name.length > 13) {
              name = `${capitalize(name?.substring(0, 5))}...`;
            } else {
              name = capitalize(name);
            }

            // Check if overallSentiment is present in the color map
            // eslint-disable-next-line no-prototype-builtins
            if (OVERALL_SENTIMENT_TEXT_COLOR.hasOwnProperty(overallSentiment)) {
              const textColor = OVERALL_SENTIMENT_TEXT_COLOR[overallSentiment];
              return `<span style="color:${textColor}">${name}</span>`;
            }
            // Fallback to default color if overallSentiment is not in the color map
            return `<span style="color:#666666">${name}</span>`;
          },
          style: {
            textOutline: 'none',
            fontWeight: 'bold',
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    title: {
      text: null,
    },
    legend: {
      enabled: false,
    },

    tooltip: {
      backgroundColor: '#ffffff',
      borderRadius: 12,
      borderColor: '#F2F2F2',
      padding: 12,
      shape: 'square',
      style: {
        minHeight: '250px',
        width: '326px',
        boxShadow: '0 3px 14px rgb(0 0 0 / 40%)',
      },
      formatter() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const chart = this;
        const { point, key, color } = chart ?? {};
        const { overallSentiment, totalAgendaPoints, totalOneOnOnes, sentimentValue } = point ?? {};
        const header = `<div class="highcharts-tooltip-flex">
        <div class="highcharts-tooltip-edge" style="background-color: ${color}; height: 150px;"></div>
        <div class="highcharts-tooltip-flex-column">
        <b>Theme: ${_.startCase(_.toLower(key))} </b>
        <br/><hr/><br/>
        <p>Number of agenda points: ${totalAgendaPoints}</p>
        <br/
        <p>Number of 1:1s: ${totalOneOnOnes}</p>
        <br/><hr/><br/>
        <b>Overall Sentiment: ${overallSentiment}</b><br/>  
        ${sentimentValue != null ? `<p>${overallSentiment}: ${sentimentValue}%</p>` : ''}<br/>    
        </div>
        </div>
        `;
        return header;
      },

      useHTML: true,
    },

    series: chartData,
  };

  if (!themeSentimentData || Object.keys(themeSentimentData).length === 0) {
    return (
      <CardContent>
        <EmptyScreen
          illustrationSrc={checkNewNavEnabled() ? noInsights : NoDataImg}
          isSmallTextSize
          heading="No data to show topic distribution"
        />
      </CardContent>
    );
  }

  return (
    <div ref={chartRef} style={{ height: '100%' }}>
      <HighchartsReact
        ref={chartRef}
        highcharts={Highcharts}
        options={options}
        containerProps={{ style: { height: '85%', width: '100%' } }}
      />
      <SentimentLegend isMyDataOverviewChart={isMyDataOverviewChart} />
    </div>
  );
};

export default ThemeDistributionChart;
